import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchLanguages } from '../services/ttsApi'
import { TtsLanguage, TtsVoice, UNKNOWN_LANG, voiceLanguageBuckets } from '../types/tts'

// Re-exported for the components that render the synthetic bucket.
export { UNKNOWN_LANG } from '../types/tts'

export interface UseTtsLanguagesResult {
  /** Union of the /v1/tts/languages list and voice-derived languages, deduped by
   *  code, with `voiceCount` reflecting the actually-loaded voices. */
  languageOptions: TtsLanguage[]
  /** True while the languages endpoint is still loading AND we have no options. */
  languagesLoading: boolean
  /** Preferred initial language: one that HAS voices before an empty fallback;
   *  '' when there are no languages at all. */
  preferredLanguage: string
}

const isLowLatency = (code: string) => code === 'en' || code === 'fr'

/** Voice counts per language bucket, including the synthetic 'unknown' one. A
 *  multi-language voice counts under EVERY language it advertises, matching the
 *  list the user actually gets when they pick that language. */
export function countVoicesByLanguage(stockVoices: TtsVoice[]): Record<string, number> {
  const counts: Record<string, number> = {}
  stockVoices.forEach((v) => {
    voiceLanguageBuckets(v).forEach((code) => {
      counts[code] = (counts[code] || 0) + 1
    })
  })
  return counts
}

/**
 * MERGE (union) the endpoint's language list with the languages derived from the
 * loaded voices, deduped by code. A language that has voices but is missing from
 * the endpoint still gets a chip, and `voiceCount` always reflects the loaded
 * voices so `count > 0` checks work even when the endpoint omits counts.
 *
 * Pure so the merge, the orphan bucket, and the counts can be tested directly.
 *
 * @param otherLabel display name for the synthetic unresolved-language bucket.
 */
export function buildLanguageOptions(
  fetchedLanguages: TtsLanguage[],
  stockVoices: TtsVoice[],
  otherLabel: string
): TtsLanguage[] {
  const voiceCountByLang = countVoicesByLanguage(stockVoices)
  const byCode = new Map<string, TtsLanguage>()

  // Start from the endpoint list (preserves its ordering + native names).
  fetchedLanguages.forEach((l) => {
    byCode.set(l.code, { ...l })
  })

  // Union in real (non-orphan) languages that only exist in the loaded voices.
  Object.keys(voiceCountByLang).forEach((code) => {
    if (code === UNKNOWN_LANG) return
    if (!byCode.has(code)) {
      byCode.set(code, {
        code,
        name: code.toUpperCase(),
        streaming: true,
        lowLatency: isLowLatency(code),
      })
    }
  })

  // The count is ALWAYS the locally-derived one. The endpoint's `voiceCount` is
  // never trusted: before the catalog loads (or when the two disagree) it would
  // advertise voices this client cannot show, which is exactly how a chip ends up
  // promising N voices and then rendering "no voices available for this language".
  const merged = Array.from(byCode.values()).map((l) => ({
    ...l,
    voiceCount: voiceCountByLang[l.code] ?? 0,
  }))

  // Orphan bucket — only when voices with an unresolved language actually exist,
  // and always last so it can never become the default or lead the list. With a
  // healthy catalog this bucket is empty and never rendered.
  if ((voiceCountByLang[UNKNOWN_LANG] ?? 0) > 0) {
    merged.push({
      code: UNKNOWN_LANG,
      name: otherLabel,
      voiceCount: voiceCountByLang[UNKNOWN_LANG],
      streaming: true,
      lowLatency: false,
    })
  }

  return merged
}

/**
 * Preferred initial language: a low-latency language that has voices, then any
 * language with voices, then — if NO real language has voices at all — the
 * unresolved-language bucket when it does, then a low-latency one (even if
 * empty), then whatever is first.
 *
 * Ordering matters in both directions. The 'unknown' bucket must never win while
 * a real language has voices (a catalog glitch can't land the user in "Other"),
 * but it must beat an EMPTY real language: when catalog drift dumps every voice
 * into the bucket, defaulting to a voiceless language is the "no voices
 * available for this language yet" dead end this whole flow exists to avoid.
 */
export function pickPreferredLanguage(languageOptions: TtsLanguage[]): string {
  if (languageOptions.length === 0) return ''
  const real = languageOptions.filter((l) => l.code !== UNKNOWN_LANG)
  const hasVoices = (l: TtsLanguage) => (l.voiceCount ?? 0) > 0
  const unknownWithVoices = languageOptions.find((l) => l.code === UNKNOWN_LANG && hasVoices(l))
  const preferred =
    real.find((l) => l.lowLatency && hasVoices(l)) ||
    real.find(hasVoices) ||
    unknownWithVoices ||
    real.find((l) => l.lowLatency) ||
    real[0] ||
    languageOptions[0]
  return preferred?.code ?? ''
}

/**
 * Shared language-list plumbing for the language-first TTS surfaces (gallery +
 * playground). Fetches the supported-language list, merges it with the languages
 * derived from the loaded stock voices, and derives the preferred default. This
 * lives in one place so the merge, the orphan bucket, and the has-voices-first
 * default can't drift apart.
 *
 * @param stockVoices the loaded STOCK voices (cloned voices excluded by the caller).
 */
export function useTtsLanguages(stockVoices: TtsVoice[]): UseTtsLanguagesResult {
  const { t } = useTranslation()
  const [fetchedLanguages, setFetchedLanguages] = useState<TtsLanguage[]>([])
  const [languagesLoading, setLanguagesLoading] = useState(true)

  // Fetch the supported-language list; on failure we still derive from voices.
  useEffect(() => {
    let cancelled = false
    fetchLanguages()
      .then((langs) => {
        if (!cancelled) setFetchedLanguages(langs)
      })
      .catch(() => {
        /* keep catalog-derived fallback */
      })
      .finally(() => {
        if (!cancelled) setLanguagesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const otherLabel = t('tts.otherLanguage', 'Other (language not recognized)')

  const languageOptions = useMemo(
    () => buildLanguageOptions(fetchedLanguages, stockVoices, otherLabel),
    [fetchedLanguages, stockVoices, otherLabel]
  )

  const preferredLanguage = useMemo(() => pickPreferredLanguage(languageOptions), [languageOptions])

  return { languageOptions, languagesLoading, preferredLanguage }
}

export default useTtsLanguages
